import {
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Card as MuiCard,
  CardContent,
  Chip,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Link,
  Typography,
} from '@material-ui/core'
import {
  Add as AddIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Edit as EditIcon,
  Home as HomeIcon,
  CloudDownload as MuiCloudDownload,
} from '@material-ui/icons'
import { spacing } from '@material-ui/system'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useQuery, UseQueryResult } from 'react-query'
import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { ParcelOrder, Tag } from 'src/api/models'
import { queryClient } from 'src/App'
import { ParcelOrderDialog } from 'src/modules/client/components/parcel/OrderDialog'
import { AddAttachmentsDialog } from 'src/components/Dialog/AddAttachmentsDialog'
import { GenericPreview, PaginatedFilePreviewGallery } from 'src/components/FilePreview'
import {
  AddressField,
  DateField,
  DateTimeField,
  DetailTable,
  LinkedField,
  OrderStatusField,
  PaginatedTable,
  RelatedLinkedField,
  useQueryPaginator,
  ValueField,
} from 'src/components/Table'
import { skuit } from 'src/services/skuit'
import { ExportDialog } from 'src/components/Dialog'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Button = styled(MuiButton)(spacing)

const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing)

const CloudDownload = styled(MuiCloudDownload)(spacing)

const Centered = styled.div`
  text-align: center;
`

interface DetailsProps {
  order: UseQueryResult<ParcelOrder>
  customFieldValues: Record<string, string>
  refresh: () => void
}

const Details = (props: DetailsProps) => {
  const { order, customFieldValues, refresh } = props
  const [OrderEditDialogOpen, setOrderEditDialogOpen] = useState(false)
  return (
    <Card mb={6}>
      {OrderEditDialogOpen && (
        <ParcelOrderDialog
          open={OrderEditDialogOpen}
          handleClose={() => {
            setOrderEditDialogOpen(false)
          }}
          update
          order={order.data}
          onSave={refresh}
        />
      )}
      <CardContent>
        <Centered>
          <HomeIcon />
          <Typography variant="h6" gutterBottom>
            {!!order && order.data?.name}
          </Typography>
          {!!order && order.data?.status === 'received' && (
            <>
              <IconButton size="small" onClick={() => setOrderEditDialogOpen(true)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => skuit.client.parcel.orders.autoFulfill(order.data?.guid || '')}>
                <AssignmentTurnedInIcon fontSize="small" />
              </IconButton>
            </>
          )}
          <DetailTable
            obj={order}
            fields={[
              { id: 'guid', label: 'SquID', renderer: ValueField },
              { id: 'number', label: 'Number', renderer: ValueField },
              { id: 'carrier.name', label: 'Carrier', renderer: ValueField },
              { id: 'placedOn', label: 'Date Placed', renderer: DateTimeField },
              { id: 'fulfillOn', label: 'Date to Fulfill On', renderer: DateField },
              { id: 'details', label: 'Details', renderer: ValueField },
              { id: 'address', label: 'Address', renderer: AddressField },
              { id: 'status', label: 'Status', renderer: OrderStatusField },
            ]}
            customFields={customFieldValues}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface PathParams {
  guid: string
}

export const OrderPage = () => {
  const { guid } = useParams<PathParams>()
  const [orderDialogOpen, setOrderDialogOpen] = useState(false)
  const [addAttachmentDialogOpen, setAddAttachmentDialogOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)

  const order = useQuery<ParcelOrder>(['order'], () => skuit.client.parcel.orders.retrieve(guid))

  const attachmentsPaginator = useQueryPaginator(
    'order-attachments',
    () => skuit.client.parcel.orders.attachments.list(guid),
    {
      params: { page_size: 4, orders: guid },
    }
  )
  const shipmentsPaginator = useQueryPaginator(
    'order-shipments',
    skuit.client.parcel.shipments.list,
    {
      params: { page_size: 5, orders: guid },
    }
  )
  const unitsPaginator = useQueryPaginator(
    'order-units',
    skuit.client.parcel.orders.units.list,
    {
      params: { page_size: 5, orders: guid, 'on-container': false },
    }
  )
  const barcodedPaginator = useQueryPaginator(
    'order-barcoded-units',
    skuit.client.parcel.orders.barcodedUnits.list,
    {
      params: { page_size: 5, orders: guid },
    }
  )

  const refresh = () => {
    queryClient.invalidateQueries(['order'])
    attachmentsPaginator.refresh()
    unitsPaginator.refresh()
    barcodedPaginator.refresh()
    shipmentsPaginator.refresh()
  }

  const customFieldValues: Record<string, string> = {}
  if (order.data?.customFields?.length) {
    for (const field of order.data?.customFields) {
      customFieldValues[field.label] = field.value
    }
  }

  const exportFilters = { "guid": order.data?.guid }

  return (
    <>
      <Helmet title="Parcel Order" />
      {orderDialogOpen && (
        <ParcelOrderDialog
          open={orderDialogOpen}
          handleClose={() => {
            setOrderDialogOpen(false)
          }}
        />
      )}
      {addAttachmentDialogOpen && (
        <AddAttachmentsDialog
          onSuccess={refresh}
          addAttachment={(config) =>
            skuit.client.parcel.orders.attachments.create(guid, config?.data, config)
          }
          open={addAttachmentDialogOpen}
          handleClose={() => {
            setAddAttachmentDialogOpen(false)
          }}
        />
      )}

      <Grid justify="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Parcel Order
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} exact to="/">
              Dashboard
            </Link>
            <Link component={NavLink} exact to="/parcel/orders">
              Parcel Orders
            </Link>
            <Typography>{order && order.data?.name}</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <Grid container justify="flex-end" spacing={6}>
            <Grid item>
              <Grid container justify="flex-end">
                <Button variant="contained" onClick={() => setExportOpen(true)}>
                  <CloudDownload mr={2} /> Export Parcel Order Fulfillment Report
                </Button>
              </Grid>
            </Grid>

            <Grid item>
              <div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOrderDialogOpen(true)}
                >
                  <AddIcon />
                  New Parcel Order
                </Button>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Grid container spacing={6}>
            <Grid item xs={12} lg={6}>
              {order && <Details order={order} refresh={refresh} customFieldValues={customFieldValues} />}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <PaginatedFilePreviewGallery
            title="Attachment"
            paginator={attachmentsPaginator}
            getIdent={(item) => item.guid}
            onAdd={() => {
              setAddAttachmentDialogOpen(true)
            }}
            renderItem={(item) => {
              return (
                <>
                  <GenericPreview url={item.file} />
                  <div>
                    {item.tags.map((tag: Tag) => (
                      <Chip key={tag.slug} label={tag.name} />
                    ))}
                  </div>
                </>
              )
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Shipments"
            fields={[
              { id: 'trackingNumber', label: 'Tracking Number', renderer: LinkedField(ValueField) },
              { id: 'consignee', label: 'Consignee', renderer: ValueField },
              { id: 'facility.name', label: 'Facility', renderer: ValueField },
              { id: 'carrier.name', label: 'Carrier', renderer: ValueField },
              { id: 'status', label: 'Status', renderer: ValueField },
            ]}
            paginator={shipmentsPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Units"
            fields={[
              { id: 'sku.productName', label: 'Product Name', renderer: ValueField },
              { id: 'product', label: 'Product', renderer: RelatedLinkedField(ValueField, 'name') },
              { id: 'sku', label: 'Sku', renderer: RelatedLinkedField(ValueField, 'sku') },
              { id: 'numUnits', label: 'Number of Units', renderer: ValueField },
            ]}
            paginator={unitsPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Units (Barcoded)"
            fields={[
              { id: 'unit', label: 'Name', renderer: RelatedLinkedField(ValueField, 'name') },
              {
                id: 'unit.product',
                label: 'Product',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
            ]}
            paginator={barcodedPaginator}
          />
        </Grid>
      </Grid>
      <ExportDialog
        name="Parcel Order Report"
        download={skuit.client.parcel.orders.exportReport}
        open={exportOpen}
        fields={['sku', 'num_requested', 'num_picked', 'num_fulfilled']}
        exportFilters={exportFilters}
        handleClose={() => setExportOpen(false)}
      />
    </>
  )
}
